import React, { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Avatar from "./Avatar"
import { fetchAssignableOptions } from "../api/tasks"

const computePosition = anchor => {
  if (!anchor) return { top: 0, left: 0 }
  const rect = anchor.getBoundingClientRect()
  const W = 320, H = 360
  let left = Math.max(8, rect.left)
  let top  = rect.bottom + 6
  if (left + W > window.innerWidth - 8)  left = Math.max(8, window.innerWidth - W - 8)
  if (top  + H > window.innerHeight - 8) top  = Math.max(8, rect.top - H - 6)
  return { top, left }
}

const AssignablePicker = ({ projectId, anchorEl, value, onPick, onClear, onClose }) => {
  const [options, setOptions] = useState({ users: [], suppliers: [] })
  const [stage,   setStage]   = useState(value?.kind || null) // null | "user" | "supplier"
  const [query,   setQuery]   = useState("")
  const [pos,     setPos]     = useState(() => computePosition(anchorEl))
  const ref       = useRef(null)
  const inputRef  = useRef(null)
  const listRef   = useRef(null)

  useEffect(() => {
    fetchAssignableOptions(projectId).then(setOptions).catch(() => {})
  }, [projectId])

  useEffect(() => {
    setPos(computePosition(anchorEl))
    const handler = e => {
      if (!ref.current) return
      if (!ref.current.contains(e.target) && !anchorEl?.contains(e.target)) onClose()
    }
    const esc = e => { if (e.key === "Escape") onClose() }
    document.addEventListener("mousedown", handler)
    document.addEventListener("keydown", esc)
    return () => {
      document.removeEventListener("mousedown", handler)
      document.removeEventListener("keydown", esc)
    }
  }, [anchorEl, onClose])

  useEffect(() => {
    if (!stage) return
    inputRef.current?.focus({ preventScroll: true })
    if (listRef.current) listRef.current.scrollTop = 0
  }, [stage])

  const list = stage === "user" ? options.users : stage === "supplier" ? options.suppliers : []
  const filtered = query
    ? list.filter(item => item.name.toLowerCase().includes(query.toLowerCase()))
    : list
  const tagged = filtered.map(item => ({ ...item, kind: stage }))

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[2000] w-[320px] max-h-[360px] bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col"
      style={{ top: pos.top, left: pos.left }}
    >
      {!stage ? (
        <div className="p-3 flex flex-col gap-2">
          <div className="text-[11px] uppercase tracking-wider font-medium text-slate-500 px-1">{I18n.t("projects.tasks.assignable.prompt")}</div>
          <button
            type="button"
            onClick={() => setStage("user")}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left"
          >
            <span className="w-8 h-8 grid place-items-center rounded-full bg-blue-50 text-blue-600">
              <FontAwesomeIcon icon="user" />
            </span>
            <span className="flex-1">
              <div className="text-[13px] font-medium text-slate-900">{I18n.t("projects.tasks.assignable.internal")}</div>
              <div className="text-[11.5px] text-slate-500">{I18n.t("projects.tasks.assignable.internal_hint")}</div>
            </span>
          </button>
          <button
            type="button"
            onClick={() => setStage("supplier")}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left"
          >
            <span className="w-8 h-8 grid place-items-center rounded-full bg-amber-50 text-amber-600">
              <FontAwesomeIcon icon="truck" />
            </span>
            <span className="flex-1">
              <div className="text-[13px] font-medium text-slate-900">{I18n.t("projects.tasks.assignable.subcontracted")}</div>
              <div className="text-[11.5px] text-slate-500">{I18n.t("projects.tasks.assignable.subcontracted_hint")}</div>
            </span>
          </button>
          {value && (
            <button
              type="button"
              onClick={() => { onClear(); onClose() }}
              className="text-[12px] text-rose-600 hover:text-rose-700 px-1 mt-1"
            >
              {I18n.t("projects.tasks.assignable.clear")}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="p-2.5 border-b border-slate-100 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setStage(null); setQuery("") }}
              className="text-slate-400 hover:text-slate-700 px-1"
              title={I18n.t("projects.tasks.assignable.back")}
            >
              <FontAwesomeIcon icon="chevron-left" />
            </button>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={stage === "user" ? I18n.t("projects.tasks.assignable.search_user") : I18n.t("projects.tasks.assignable.search_supplier")}
              className="flex-1 px-2 py-1 bg-slate-50 rounded text-[12.5px] outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div ref={listRef} className="overflow-y-auto flex-1 py-1">
            {tagged.length === 0 ? (
              <div className="text-center text-[12px] text-slate-400 py-6">{I18n.t("projects.tasks.assignable.no_results")}</div>
            ) : tagged.map(item => (
              <button
                key={`${item.kind}-${item.id}`}
                type="button"
                onClick={() => { onPick(item); onClose() }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left hover:bg-slate-50"
              >
                {stage === "user"
                  ? <Avatar name={item.name} size="sm" ring={false} />
                  : <span className="w-6 h-6 grid place-items-center rounded-full bg-amber-50 text-amber-600 text-[11px]"><FontAwesomeIcon icon="truck" /></span>}
                <span className="text-[12.5px] text-slate-800 truncate">{item.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  )
}

export default AssignablePicker
